import { Icon } from './icons'

/**
 * The few pieces every screen is built from, so the screens look like one app.
 *
 * Metrics come from Cadence and Talos (`.claude` design study, 2026-10-22):
 * IBM Plex at 14/12/13 on a desktop, nothing heavier than 600, radii of 6 and
 * 8, no drop shadows on anything that is not floating, and **one panel around
 * a list rather than a card per item**. The CSS is in `app.css` under "the page
 * frame"; these are the shapes that use it.
 *
 * Kept small on purpose. A screen that needs something these do not cover
 * writes it in its own file and its own stylesheet — this is not a framework.
 */

export function PageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
}: {
  eyebrow?: React.ReactNode
  title: React.ReactNode
  subtitle?: React.ReactNode
  actions?: React.ReactNode
}): JSX.Element {
  return (
    <header className="page-header">
      <div className="page-header__text">
        {eyebrow ? <span className="page-header__eyebrow">{eyebrow}</span> : null}
        <h1 className="page-header__title">{title}</h1>
        {subtitle ? <p className="page-header__subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="page-header__actions">{actions}</div> : null}
    </header>
  )
}

/** A labelled group of content: small capitals above, the content below. */
export function Section({
  label,
  actions,
  children,
}: {
  label: React.ReactNode
  actions?: React.ReactNode
  children: React.ReactNode
}): JSX.Element {
  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section-label">{label}</h2>
        {actions}
      </div>
      {children}
    </section>
  )
}

/** One bordered surface around a set of rows or a table. */
export function Panel({ children, padded = false }: { children: React.ReactNode; padded?: boolean }): JSX.Element {
  return <div className={`panel${padded ? ' panel--padded' : ''}`}>{children}</div>
}

/**
 * Nothing here yet — said once, quietly, with the one thing to do about it.
 * Never a badge, never a paragraph about milestones.
 */
export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon?: string
  title: React.ReactNode
  children?: React.ReactNode
  action?: React.ReactNode
}): JSX.Element {
  return (
    <div className="empty-state">
      {icon ? <Icon name={icon} size={28} /> : null}
      <span className="empty-state__title">{title}</span>
      {children ? <p>{children}</p> : null}
      {action}
    </div>
  )
}

export type Tone = 'neutral' | 'healthy' | 'warning' | 'critical' | 'info'

/** A state on a thing. Squared, small, edged in its own hue. */
export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: React.ReactNode }): JSX.Element {
  return <span className={`badge${tone === 'neutral' ? '' : ` ${tone}`}`}>{children}</span>
}

export function StatTile({
  label,
  value,
  sub,
  warning = false,
}: {
  label: React.ReactNode
  value: React.ReactNode
  sub?: React.ReactNode
  warning?: boolean
}): JSX.Element {
  return (
    <div className={`stat-tile${warning ? ' stat-tile--warning' : ''}`}>
      <span className="stat-tile__label">{label}</span>
      <span className="stat-tile__value">{value}</span>
      {sub ? <span className="muted">{sub}</span> : null}
    </div>
  )
}
